import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { EventClient } from '@aicores/events';
import type { Module } from './types';
import { IconInfo } from './icons';

interface Props {
  module: Module;
  events: EventClient;
  isSecondary?: boolean;
  children: ReactNode;
}

export function ModuleSection({ module, events, isSecondary, children }: Props) {
  const expandedOnce = useRef(false);
  const el = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!el.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !expandedOnce.current) {
          expandedOnce.current = true;
          events.fire('module_expanded', {
            module_id: module.module_id,
            module_index: module.index,
          });
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el.current);
    return () => observer.disconnect();
  }, [events, module.module_id, module.index]);

  const classes = ['module'];
  if (module.is_foundation) classes.push('is-foundation');
  if (isSecondary) classes.push('is-secondary');

  return (
    <section
      ref={el}
      className={classes.join(' ')}
      id={`module-${module.module_id}`}
      data-module-id={module.module_id}
    >
      <div className="module-header">
        <div className="module-number">PART {String(module.index).padStart(2, '0')}</div>
        <div className="module-meta">
          <h3 className="module-title">{module.title.replace(/^Part \d+\s*—\s*/, '')}</h3>
          <div className="module-subtitle">
            {module.subtitle} · {module.prompt_ids.length}{' '}
            {module.prompt_ids.length === 1 ? 'prompt' : 'prompts'}
          </div>
          <p className="module-description">{module.description}</p>
        </div>
      </div>

      {module.is_foundation && module.sequence_callout ? (
        <div className="sequence-callout">
          <IconInfo className="sequence-callout-icon" aria-hidden="true" />
          <div
            className="sequence-callout-text"
            dangerouslySetInnerHTML={{ __html: module.sequence_callout }}
          />
        </div>
      ) : null}

      <div className="prompts">{children}</div>
    </section>
  );
}
